import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Check, X, Eye, Ban, RotateCcw, Save, Wallet, Gavel, Tag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  adminGetInvestorDetail, adminUpdateInvestor, adminSuspendInvestor, adminReactivateInvestor,
  adminApproveKyc, adminRejectKyc, adminKycDocumentBlob,
} from '../lib/api';

const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const inputCls = 'border-white/15 bg-white/5 text-farmway-cream placeholder:text-farmway-cream/40 focus-visible:ring-farmway-gold';

const KYC_LABELS = [
  ['fullName', 'Full legal name'], ['dob', 'Date of birth'], ['country', 'Country'],
  ['idType', 'ID type'], ['idNumber', 'ID number'], ['address', 'Address'],
];

const STATUS_BADGE_CLS = {
  onboarded: 'bg-farmway-leaf/20 text-farmway-leaf-bright',
  suspended: 'bg-red-500/20 text-red-400',
};
const KYC_BADGE_CLS = {
  approved: 'bg-farmway-leaf/20 text-farmway-leaf-bright',
  submitted: 'bg-farmway-gold/20 text-farmway-gold',
  rejected: 'bg-red-500/20 text-red-400',
};

const StatusBadge = ({ status }) => (
  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_BADGE_CLS[status] || 'bg-white/10 text-farmway-cream/60'}`}>{status}</span>
);

const KycBadge = ({ status }) => (
  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${KYC_BADGE_CLS[status] || 'bg-white/10 text-farmway-cream/50'}`}>KYC {status === 'not_started' || !status ? 'pending' : status}</span>
);

export default function InvestorDialog({ userId, open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: '', email: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await adminGetInvestorDetail(userId);
      setData(d);
      setForm({ name: d.user.name || '', email: d.user.email || '' });
    } catch { setData(null); }
  }, [userId]);

  useEffect(() => {
    if (open && userId) load();
    else setData(null);
  }, [open, userId, load]);

  const u = data?.user;
  const kyc = u?.kyc || {};
  const kycStatus = kyc.status || 'not_started';

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); await load(); onChanged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateInvestor(userId, form);
      toast.success('Investor updated');
      await load();
      onChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid input, check the email address');
    }
    finally { setSaving(false); }
  };

  const rejectKyc = () => {
    const reason = window.prompt('Reason for rejection (optional):');
    if (reason === null) return;
    act(() => adminRejectKyc(userId, reason), 'KYC rejected');
  };

  const viewId = async () => {
    try {
      const blob = await adminKycDocumentBlob(userId);
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Could not load KYC document'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="investor-dialog" className="max-h-[85vh] max-w-2xl overflow-y-auto border-white/10 bg-farmway-green-dark text-farmway-cream">
        <DialogDescription className="sr-only">Investor management details</DialogDescription>
        {!u ? (
          <>
            <DialogHeader className="sr-only"><DialogTitle>Investor</DialogTitle></DialogHeader>
            <p className="py-8 text-farmway-cream/50">Loading…</p>
          </>
        ) : (
          <>
            <DialogHeader>
              <span className="eyebrow">Investor</span>
              <DialogTitle className="flex flex-wrap items-center gap-3 font-display text-2xl font-black uppercase tracking-tight">
                {u.name} <StatusBadge status={u.status} /> <KycBadge status={kycStatus} />
              </DialogTitle>
            </DialogHeader>
            <p className="-mt-2 text-sm text-farmway-cream/50">{u.email}</p>

            {/* edit details */}
            <div className="space-y-3 rounded-2xl border border-white/10 bg-white/5 p-4">
              <h4 className="font-display text-sm font-bold uppercase tracking-wider text-farmway-cream/70">Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-farmway-cream/80">Name</Label><Input data-testid="inv-edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
                <div className="space-y-1.5"><Label className="text-farmway-cream/80">Email</Label><Input data-testid="inv-edit-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></div>
              </div>
              <p className="text-[11px] text-farmway-cream/40">Changing the email moves their holdings and marketplace activity to the new address; they must sign in with Google using it.</p>
              <div className="flex flex-wrap gap-2">
                <button data-testid="inv-save-btn" onClick={save} disabled={saving} className="btn-gold disabled:opacity-70"><Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save changes'}</button>
                {u.status === 'suspended' ? (
                  <button data-testid="inv-reactivate-btn" onClick={() => act(() => adminReactivateInvestor(userId), 'Investor reactivated')} className="btn-outline"><RotateCcw className="h-4 w-4" /> Reactivate</button>
                ) : (
                  <button data-testid="inv-suspend-btn" onClick={() => { if (window.confirm(`Suspend ${u.email}? They will lose access immediately.`)) act(() => adminSuspendInvestor(userId), 'Investor suspended'); }} className="inline-flex items-center gap-2 rounded-full border border-red-400/40 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-red-400 transition-colors hover:bg-red-500/10"><Ban className="h-4 w-4" /> Suspend</button>
                )}
              </div>
            </div>

            {/* KYC */}
            <div className="space-y-3 rounded-2xl border border-white/10 bg-white/5 p-4">
              <h4 className="font-display text-sm font-bold uppercase tracking-wider text-farmway-cream/70">KYC verification</h4>
              {kycStatus === 'not_started' ? (
                <p className="text-sm text-farmway-cream/50">Not submitted yet.</p>
              ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {KYC_LABELS.map(([k, label]) => kyc[k] ? (
                    <div key={k}><p className="text-[10px] uppercase tracking-wider text-farmway-cream/40">{label}</p><p className="text-farmway-cream/85">{kyc[k]}</p></div>
                  ) : null)}
                  {kyc.submitted_at && <div><p className="text-[10px] uppercase tracking-wider text-farmway-cream/40">Submitted</p><p className="text-farmway-cream/85">{new Date(kyc.submitted_at).toLocaleDateString()}</p></div>}
                </div>
              )}
              {kycStatus === 'rejected' && kyc.rejectReason && (
                <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-300">Rejected: {kyc.rejectReason}</p>
              )}
              <div className="flex flex-wrap gap-2">
                {kyc.document && (
                  <button data-testid="inv-view-id-btn" onClick={viewId} className="btn-outline"><Eye className="h-4 w-4" /> View ID document</button>
                )}
                {['submitted', 'rejected'].includes(kycStatus) && (
                  <button data-testid="inv-kyc-approve-btn" onClick={() => act(() => adminApproveKyc(userId), 'KYC approved')} className="btn-gold"><Check className="h-4 w-4" /> Approve</button>
                )}
                {kycStatus === 'submitted' && (
                  <button data-testid="inv-kyc-reject-btn" onClick={rejectKyc} className="inline-flex items-center gap-2 rounded-full border border-red-400/40 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-red-400 transition-colors hover:bg-red-500/10"><X className="h-4 w-4" /> Reject</button>
                )}
              </div>
            </div>

            {/* holdings */}
            <div className="space-y-3 rounded-2xl border border-white/10 bg-white/5 p-4">
              <h4 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-farmway-cream/70"><Wallet className="h-4 w-4 text-farmway-gold" /> Holdings ({data.holdings.length})</h4>
              {data.holdings.length === 0 ? <p className="text-sm text-farmway-cream/50">No holdings.</p> : (
                <div className="space-y-1.5">
                  {data.holdings.map((h) => (
                    <div key={h.id} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-sm">
                      <span className="text-farmway-cream/85">{h.projectName} <span className="text-farmway-cream/40">· {h.units} units</span>{h.listed && <span className="ml-2 rounded-full bg-farmway-gold/20 px-2 py-0.5 text-[9px] font-bold uppercase text-farmway-gold">listed</span>}</span>
                      <span className="font-display font-bold text-farmway-cream">{money(h.currentValue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* marketplace activity */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-farmway-cream/40"><Tag className="h-3.5 w-3.5 text-farmway-gold" /> Listings</p>
                <p className="mt-1 font-display text-2xl font-black text-farmway-cream">{data.listings.length}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-farmway-cream/40"><Gavel className="h-3.5 w-3.5 text-farmway-gold" /> Bids placed</p>
                <p className="mt-1 font-display text-2xl font-black text-farmway-cream">{data.bids.length}</p>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
